import { useCheckIsIdle } from '@/context/services/hooks/useCheckIsIdle';
import { getPageData } from '@/utils/getPageData';
import { QueryKey } from '@tanstack/react-query';
import { useGetProjectsSuspenseInfinite } from 'ap-openapi';
import { useEffect, useMemo } from 'react';

const PROJECTS_PER_PAGE = 1000;

const getProjectsNextPageParam = (page: {
  data: { next_cursor?: string | null };
}): string | undefined => {
  return page.data.next_cursor ?? undefined;
};

export const useGetProjectsAllSuspense = () => {
  const checkIsIdle = useCheckIsIdle();

  const { data, error, fetchNextPage, hasNextPage, queryKey } =
    useGetProjectsSuspenseInfinite(
      {
        per: PROJECTS_PER_PAGE,
      },
      {
        query: {
          getNextPageParam: getProjectsNextPageParam,
          staleTime: Infinity,
        },
      }
    );
  if (error) throw error;
  useEffect(() => {
    if (hasNextPage && checkIsIdle(queryKey)) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, checkIsIdle, queryKey]);
  return useMemo(() => getPageData(data, 'projects'), [data]);
};

type UseGetProjectsWithAgentApplicantResourceOptions = {
  queryKey?: QueryKey;
};

export const useGetProjectsWithAgentApplicantResource = (
  options?: UseGetProjectsWithAgentApplicantResourceOptions
) => {
  const { queryKey } = options || {};
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = useGetProjectsSuspenseInfinite(
    {
      per: PROJECTS_PER_PAGE,
    },
    {
      query: {
        getNextPageParam: getProjectsNextPageParam,
        queryKey,
      },
      request: {
        headers: {
          agentApplicantResource: 'project',
        },
      },
    }
  );
  if (error) throw error;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const items = useMemo(() => getPageData(data, 'projects'), [data]);

  return {
    items,
    isLoading: isFetching || isFetchingNextPage || hasNextPage,
  };
};
